import { groupBy } from 'lodash'
import { ILoyaltyCampaignRuleMapResponse } from 'features/Loyalty/types'
import {
  GTLoyaltyCampaignConditions,
  GTLoyaltyCampaignPlace,
  GTLoyaltyCampaignRule,
  GTLoyaltyCampaignRuleConditionsCitySegment,
  GTSegment
} from 'gql/types.generated'
import { isEmpty } from 'lodash'
import { IFormSegment } from 'features/Loyalty/components/CreateRule/types'

export interface IGetCitySegmentsForMutationInput {
  allCitiesAndSegments: boolean
  allSegments: boolean
  cityOrigins: GTLoyaltyCampaignPlace[]
  cityDestinations: GTLoyaltyCampaignPlace[]
  segments: IFormSegment[]
  isReturn: boolean
}

export type TGetCitySegmentsForMutationOutput =
  | {
      originID: string
      destinationID: string
    }[]
  | {
      originID: string
      destinationID: string
      segments: GTSegment[]
    }[]
  | null

interface TRouteSegments {
  originID: string
  destinationID: string
  segmentIDs: string[]
}
export type TGroupSegmentsByRoutesOutput = TRouteSegments[]
export type TGetRoutesWithoutSegmentsOutput = Omit<TRouteSegments, 'segmentIDs'>[]

export const isBothWays = (
  citySegments: GTLoyaltyCampaignRuleConditionsCitySegment[],
  isAllSegments: boolean
): boolean => {
  let cityOriginsIDs: string[] = []
  let cityDestinationsIDs: string[] = []
  if (isAllSegments) {
    cityOriginsIDs = citySegments.map(({ origin }) => origin.id)
    cityDestinationsIDs = citySegments.map(({ destination }) => destination.id)
  } else {
    cityOriginsIDs = citySegments.flatMap(({ segments }) => segments.map(({ origin }) => origin.id))
    cityDestinationsIDs = citySegments.flatMap(({ segments }) => segments.map(({ destination }) => destination.id))
  }
  const isReturn =
    cityOriginsIDs.every((id) => cityDestinationsIDs.includes(id)) &&
    cityOriginsIDs.length === cityDestinationsIDs.length
  return isReturn
}

type Place = { id: string }
type Segment<T extends Place> = { origin: T; destination: T }
export const getCitiesFromRespose = <T extends Place>(
  citySegments: Segment<T>[]
): {
  origins: T[]
  destinations: T[]
} => {
  const origins: T[] = []
  const destinations: T[] = []

  for (let index = 0; index < citySegments.length; index++) {
    const segment = citySegments[index]
    if (!origins.find((origin) => origin.id === segment.origin.id)) {
      origins.push(segment.origin)
    }
    if (!destinations.find((destination) => destination.id === segment.destination.id)) {
      destinations.push(segment.destination)
    }
  }

  return {
    origins,
    destinations
  }
}

export const mapLoyaltyCampaignRuleResponseToForm = (
  loyaltyCampaignRuleResponse: GTLoyaltyCampaignRule
): ILoyaltyCampaignRuleMapResponse => {
  const conditions = loyaltyCampaignRuleResponse.conditions as GTLoyaltyCampaignConditions
  const citySegments = conditions.citySegments as GTLoyaltyCampaignRuleConditionsCitySegment[]
  const isAllSegments = citySegments.every((citySegment) => citySegment?.segments.length === 0)
  const isReturn = isBothWays(citySegments, isAllSegments)
  const cities = getCitiesFromRespose(citySegments)

  return {
    ...loyaltyCampaignRuleResponse,
    allCitiesAndSegments: isEmpty(conditions.citySegments),
    allSegments: isAllSegments,
    conditions: {
      salesChannelsV2: conditions.salesChannelsV2,
      totalBookingsValue: conditions.totalBookingsValue as number,
      routes: [],
      routeServices: conditions.routeServices || [],
      citySegments: {
        destinations: cities.destinations,
        origins: cities.origins,
        segments: (conditions.citySegments as GTLoyaltyCampaignRuleConditionsCitySegment[]).reduce(
          (result: TSFixMe[], citySegment) => {
            return [
              ...result,
              ...citySegment.segments.map((segment) => {
                return {
                  ...segment,
                  cityOrigin: citySegment.origin,
                  cityDestination: citySegment.destination
                }
              })
            ]
          },
          []
        ),
        isReturn
      }
    }
  }
}

export function getRoutesWithoutSegments(
  cityOrigins: GTLoyaltyCampaignPlace[],
  cityDestinations: GTLoyaltyCampaignPlace[],
  isBothWays: boolean
): TGetRoutesWithoutSegmentsOutput {
  const result = []
  for (let i = 0; i < cityOrigins.length; i++) {
    for (let j = 0; j < cityDestinations.length; j++) {
      const cityOrigin = cityOrigins[i].id
      const cityDestination = cityDestinations[j].id
      result.push({
        originID: cityOrigin,
        destinationID: cityDestination
      })
      if (isBothWays) {
        result.push({
          originID: cityDestination,
          destinationID: cityOrigin
        })
      }
    }
  }
  return result
}

export function groupSegmentsByRoutes(segments: IFormSegment[]): TGroupSegmentsByRoutesOutput {
  const groupedSegments = groupBy(segments, (segment) => `${segment.cityOrigin.id}*${segment.cityDestination.id}`)

  const result = Object.keys(groupedSegments).map((key) => {
    const [cityOrigin, cityDestination] = key.split('*')
    const segmentIDs = groupedSegments[key].map((segment) => segment.id)

    return {
      originID: cityOrigin,
      destinationID: cityDestination,
      segmentIDs
    }
  })

  return result
}

export const getCitySegmentsForMutation = ({
  allCitiesAndSegments,
  allSegments,
  cityOrigins,
  cityDestinations,
  segments,
  isReturn
}: IGetCitySegmentsForMutationInput): TGetCitySegmentsForMutationOutput => {
  if (allCitiesAndSegments) {
    return null
  } else if (allSegments) {
    return getRoutesWithoutSegments(cityOrigins, cityDestinations, isReturn)
  }
  return groupSegmentsByRoutes(segments)
}
